import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Menu,
  Brain,
  X,
  Download,
  MessageSquare,
  UtensilsCrossed,
  BookOpen,
  LayoutGrid,
  ChevronDown,
  ChevronUp,
  FileText,
  Home,
  User,
  Calendar,
  Heart,
  Image,
  Phone,
  Camera,
  LogOut
} from "lucide-react";


import useHostelSettings from "@/hooks/useHostelSettings";

// Import parent sub-pages
import ParentHome from "@/components/parent/ParentHome";
import ChildProfile from "@/components/parent/ChildProfile";
import AttendanceView from "@/components/parent/AttendanceView";
import HealthView from "@/components/parent/HealthView";
import MarkSheetsView from "@/components/parent/MarkSheetsView";
import ActivitiesView from "@/components/parent/ActivitiesView";
import Notifications from "@/components/parent/Notifications";
import CallRequestsParent from "@/components/parent/CallRequestsParent";
import WeeklyReports from "@/components/parent/WeeklyReports";
import ParentNoticeBoard from "@/components/parent/ParentNoticeBoard";
import AiInsights from "@/components/parent/AiInsights";
import NutritionView from "@/components/parent/NutritionView";
import AcademicView from "@/components/parent/AcademicView";
import ParentGallery from "@/components/parent/ParentGallery";
import HolidayView from "@/components/shared/HolidayView";
import { Helmet } from "react-helmet";
import CommonPasswordForgotModal from "@/components/common/CommonPasswordForgotModal";

const ParentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { settings, logoUrl, shortAddress } = useHostelSettings();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);


  // Update favicon when logo changes
  useEffect(() => {
    if (logoUrl) {
      let favicon = document.querySelector('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      favicon.href = logoUrl;
    }
  }, [logoUrl]);

  const menuItems = [
    {
      icon: Home,
      label: "Dashboard",
      path: "/parent",
      testId: "nav-dashboard",
    },
    {
      icon: MessageSquare,
      label: "Notice Board",
      path: "/parent/announcements",
      testId: "nav-announcements",
    },
    {
      icon: User,
      label: "My Child",
      path: "/parent/child",
      testId: "nav-child",
    },
    {
      icon: Calendar,
      label: "Attendance",
      path: "/parent/attendance",
      testId: "nav-attendance",
    },
    {
      icon: Heart,
      label: "Health Updates",
      path: "/parent/health",
      testId: "nav-health",
    },
    {
      icon: FileText,
      label: "Mark Sheets",
      path: "/parent/marksheets",
      testId: "nav-marksheets",
    },
    {
      icon: Image,
      label: "Activities",
      path: "/parent/activities",
      testId: "nav-activities",
    },
    {
      icon: Camera,
      label: "School Gallery",
      path: "/parent/gallery",
      testId: "nav-gallery",
    },
    {
      icon: Download,
      label: "Weekly Reports",
      path: "/parent/reports",
      testId: "nav-reports",
    },
    {
      icon: Phone,
      label: "Parent Requests",
      path: "/parent/calls",
      testId: "nav-calls",
    },
    {
      icon: Brain,
      label: "AI Insights",
      path: "/parent/ai-insights",
      testId: "nav-ai-insights",
    }
  ];

  const moreItems = [
    {
      icon: UtensilsCrossed,
      label: "Nutrition",
      path: "/parent/nutrition",
      testId: "nav-nutrition",
    },
    {
      icon: BookOpen,
      label: "Academic",
      path: "/parent/academic",
      testId: "nav-academic",
    },
    {
      icon: Calendar,
      label: "Holidays",
      path: "/parent/holidays",
      testId: "nav-holidays",
    }
  ];




  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
      <Helmet>
        <title>{process.env.REACT_APP_NAME} || Parent</title>
        <link rel="icon" href="/favicon.webp" type="image/webp" />
      </Helmet>
      <div className="flex h-screen" style={{ background: "#F8FBFD" }}>
        {/* Sidebar */}
        <div
          className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
          style={{
            background: "linear-gradient(to bottom, #7ab0fe 0%, #7CB9dd 100%)",
          }}
        >
          <div className="flex flex-col h-full text-white">
            {/* Logo & Hostel Info */}
            <div className="p-6 border-b border-white/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="w-10 h-10 object-contain rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-white/20 rounded flex items-center justify-center">
                      <Home className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h1 className="text-xl font-bold leading-tight">
                      {settings.name || "DormCircle"}
                    </h1>
                    {shortAddress && (
                      <p className="text-xs opacity-90">{shortAddress}</p>
                    )}
                  </div>
                </div>
                <button
                  data-testid="close-sidebar-btn"
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm opacity-90">Parent Portal</p>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 overflow-y-auto p-4">
              <ul className="space-y-2">
                {menuItems.map((item) => (
                  <li key={item.path}>
                    <button
                      data-testid={item.testId}
                      onClick={() => {
                        navigate(item.path);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        location.pathname === item.path
                          ? "bg-white/20 shadow-md"
                          : "hover:bg-white/10"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  </li>
                ))}

                {/* More Options Section */}
                <li className="pt-2">
                  <button
                    onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all hover:bg-white/10 ${
                      moreItems.some(i => i.path === location.pathname) ? "bg-white/10" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <LayoutGrid className="w-5 h-5" />
                      <span className="font-medium">More</span>
                    </div>
                    {moreMenuOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  
                  {moreMenuOpen && (
                    <ul className="mt-2 ml-4 space-y-1 border-l border-white/20 pl-4">
                      {moreItems.map((item) => (
                        <li key={item.path}>
                          <button
                            data-testid={item.testId}
                            onClick={() => {
                              navigate(item.path);
                              setSidebarOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                              location.pathname === item.path
                                ? "bg-white/20"
                                : "hover:bg-white/10"
                            }`}
                          >
                            <item.icon className="w-4 h-4" />
                            <span className="text-sm font-medium">{item.label}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>

              </ul>
            </nav>

            {/* User Info & Logout */}
            <div className="p-4 border-t border-white/20">
              <div className="mb-4 p-3 bg-white/10 rounded-lg">
                <p className="font-semibold">{user?.name}</p>
                <p className="text-sm opacity-90">{user?.phone}</p>
              </div>
              <div className="my-5">
                <CommonPasswordForgotModal role={"parent"} />
              </div>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="outline"
                className="w-full border-white/30 text-white hover:bg-white/20"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-white shadow-sm p-4 flex items-center justify-between">
            <button
              data-testid="open-sidebar-btn"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6" style={{ color: "#2C5F7F" }} />
            </button>
            <h2 className="text-xl font-semibold" style={{ color: "#2C5F7F" }}>
              {menuItems.find((item) => item.path === location.pathname)
                ?.label || "Dashboard"}
            </h2>
            <div className="w-10"></div>
          </header>

          {/* Content Area */}
          <main className="flex-1 overflow-y-auto p-6">
            <Routes>
              <Route index element={<ParentHome />} />
              <Route path="announcements" element={<ParentNoticeBoard />} />
              <Route path="child" element={<ChildProfile />} />
              <Route path="attendance" element={<AttendanceView />} />
              <Route path="health" element={<HealthView />} />
              <Route path="nutrition" element={<NutritionView />} />
              <Route path="academic" element={<AcademicView />} />
              <Route path="marksheets" element={<MarkSheetsView />} />
              <Route path="activities" element={<ActivitiesView />} />
              <Route path="gallery" element={<ParentGallery />} />
              <Route path="reports" element={<WeeklyReports />} />
              <Route path="calls" element={<CallRequestsParent />} />
              <Route path="ai-insights" element={<AiInsights />} />
              <Route path="holidays" element={<HolidayView />} />
            </Routes>
          </main>
        </div>

        {/* Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}
      </div>
    </>
  );
};

export default ParentDashboard;
